import { ROAD_DIRECTION, type RoadDirection } from "./schema";

export type RoadSegment = readonly [
  fromX: number,
  fromZ: number,
  toX: number,
  toZ: number,
  width: number,
];

export interface RoadRecord {
  readonly tileId: string;
  readonly segment: RoadSegment;
  readonly physicalId: number;
  readonly segmentIndex: number;
  readonly travelLanes: number;
  readonly parkingLanes: number;
  readonly totalLanes: number;
  readonly direction: RoadDirection;
  readonly speedMps: number;
  readonly fromLevel: number;
  readonly toLevel: number;
}

export interface LaneEdge {
  readonly id: string;
  readonly tileId: string;
  readonly physicalId: number;
  readonly segmentIndex: number;
  readonly laneIndex: number;
  readonly laneCount: number;
  readonly direction: "FT" | "TF";
  readonly fromX: number;
  readonly fromZ: number;
  readonly toX: number;
  readonly toZ: number;
  readonly startNode: string;
  readonly endNode: string;
  readonly speedMps: number;
}

export interface VehicleRoute {
  readonly id: string;
  readonly laneIds: readonly string[];
  readonly tileIds: readonly string[];
  readonly points: Float32Array;
  readonly cumulativeLengths: Float32Array;
  readonly lengthM: number;
  readonly speedMps: number;
}

const nodeKey = (x: number, z: number, level: number): string =>
  `${Math.round(x * 4)}:${Math.round(z * 4)}:${level}`;

export function laneEdgesForRoad(road: RoadRecord): LaneEdge[] {
  const { segment, travelLanes, totalLanes, direction } = road;
  if (direction === ROAD_DIRECTION.NV || travelLanes < 1 || totalLanes < travelLanes) return [];
  if (direction === ROAD_DIRECTION.TW && travelLanes < 2) return [];
  const [fromX, fromZ, toX, toZ, width] = segment;
  const length = Math.hypot(toX - fromX, toZ - fromZ);
  if (length < 0.5) return [];
  const normalX = -(toZ - fromZ) / length;
  const normalZ = (toX - fromX) / length;
  const slotWidth = width / totalLanes;
  const offsets = Array.from(
    { length: travelLanes },
    (_, index) => (index - (travelLanes - 1) / 2) * slotWidth,
  );
  const directions: Array<{ laneIndex: number; direction: "FT" | "TF" }> = [];
  if (direction === ROAD_DIRECTION.FT || direction === ROAD_DIRECTION.TF) {
    const permittedDirection = direction === ROAD_DIRECTION.FT ? "FT" : "TF";
    for (let laneIndex = 0; laneIndex < travelLanes; laneIndex += 1)
      directions.push({ laneIndex, direction: permittedDirection });
  } else {
    const perDirection = Math.floor(travelLanes / 2);
    for (let laneIndex = 0; laneIndex < perDirection; laneIndex += 1)
      directions.push({ laneIndex, direction: "FT" });
    for (let laneIndex = travelLanes - perDirection; laneIndex < travelLanes; laneIndex += 1)
      directions.push({ laneIndex, direction: "TF" });
  }
  return directions.map(({ laneIndex, direction: laneDirection }) => {
    const offset = offsets[laneIndex] ?? 0;
    const offsetFromX = fromX + normalX * offset;
    const offsetFromZ = fromZ + normalZ * offset;
    const offsetToX = toX + normalX * offset;
    const offsetToZ = toZ + normalZ * offset;
    const forward = laneDirection === "FT";
    return {
      id: `${road.physicalId}:${road.segmentIndex}:${laneDirection}:${laneIndex}`,
      tileId: road.tileId,
      physicalId: road.physicalId,
      segmentIndex: road.segmentIndex,
      laneIndex,
      laneCount: travelLanes,
      direction: laneDirection,
      fromX: forward ? offsetFromX : offsetToX,
      fromZ: forward ? offsetFromZ : offsetToZ,
      toX: forward ? offsetToX : offsetFromX,
      toZ: forward ? offsetToZ : offsetFromZ,
      startNode: forward ? nodeKey(fromX, fromZ, road.fromLevel) : nodeKey(toX, toZ, road.toLevel),
      endNode: forward ? nodeKey(toX, toZ, road.toLevel) : nodeKey(fromX, fromZ, road.fromLevel),
      speedMps: road.speedMps,
    };
  });
}

export function createVehicleRoute(
  id: string,
  laneIds: readonly string[],
  tileIds: readonly string[],
  values: readonly number[],
  speedMps: number,
): VehicleRoute {
  if (values.length < 4 || values.length % 2 !== 0)
    throw new Error("Vehicle route requires at least two planar points");
  const points = new Float32Array(values);
  const cumulativeLengths = new Float32Array(points.length / 2);
  let lengthM = 0;

  for (let index = 1; index < cumulativeLengths.length; index += 1) {
    const offset = index * 2;
    lengthM += Math.hypot(
      (points[offset] ?? 0) - (points[offset - 2] ?? 0),
      (points[offset + 1] ?? 0) - (points[offset - 1] ?? 0),
    );
    cumulativeLengths[index] = lengthM;
  }
  if (lengthM < 0.5) throw new Error("Vehicle route is too short");
  return {
    id,
    laneIds,
    tileIds,
    points,
    cumulativeLengths,
    lengthM,
    speedMps,
  };
}

export function roadRecordsFromPacked(
  segments: Float32Array,
  attributes: Uint8Array,
  ids: Uint32Array,
  tileId: string,
): RoadRecord[] {
  const count = segments.length / 5;
  if (segments.length % 5 !== 0 || attributes.length !== count * 7 || ids.length !== count * 2)
    throw new Error("Compiled road metadata has an invalid record length");
  return Array.from({ length: count }, (_, index) => {
    const segmentOffset = index * 5;
    const attributeOffset = index * 7;
    const idOffset = index * 2;
    return {
      tileId,
      segment: [
        segments[segmentOffset] ?? 0,
        segments[segmentOffset + 1] ?? 0,
        segments[segmentOffset + 2] ?? 0,
        segments[segmentOffset + 3] ?? 0,
        segments[segmentOffset + 4] ?? 10,
      ],
      physicalId: ids[idOffset] ?? 0,
      segmentIndex: ids[idOffset + 1] ?? 0,
      travelLanes: attributes[attributeOffset] ?? 0,
      parkingLanes: attributes[attributeOffset + 1] ?? 0,
      totalLanes: attributes[attributeOffset + 2] ?? 0,
      direction: (attributes[attributeOffset + 3] ?? ROAD_DIRECTION.NV) as RoadDirection,
      speedMps: (attributes[attributeOffset + 4] ?? 25) * 0.447_04,
      fromLevel: attributes[attributeOffset + 5] ?? 13,
      toLevel: attributes[attributeOffset + 6] ?? 13,
    } satisfies RoadRecord;
  });
}

export class RoadNetwork {
  readonly lanes: readonly LaneEdge[];
  readonly #outgoing = new Map<string, LaneEdge[]>();

  constructor(roads: readonly RoadRecord[]) {
    const lanes = roads.flatMap(laneEdgesForRoad);
    this.lanes = lanes;
    for (const lane of lanes) {
      const outgoing = this.#outgoing.get(lane.startNode) ?? [];
      outgoing.push(lane);
      this.#outgoing.set(lane.startNode, outgoing);
    }
    for (const outgoing of this.#outgoing.values())
      outgoing.sort((a, b) => a.id.localeCompare(b.id));
  }

  routesNear(
    center: Readonly<{ x: number; z: number }>,
    radiusM: number,
    limit: number,
  ): VehicleRoute[] {
    const candidates = this.lanes
      .filter(
        (lane) =>
          Math.hypot(
            (lane.fromX + lane.toX) / 2 - center.x,
            (lane.fromZ + lane.toZ) / 2 - center.z,
          ) <= radiusM,
      )
      .sort(
        (left, right) =>
          Math.hypot(
            (left.fromX + left.toX) / 2 - center.x,
            (left.fromZ + left.toZ) / 2 - center.z,
          ) -
            Math.hypot(
              (right.fromX + right.toX) / 2 - center.x,
              (right.fromZ + right.toZ) / 2 - center.z,
            ) || left.id.localeCompare(right.id),
      );
    const routes: VehicleRoute[] = [];
    const signatures = new Set<string>();
    for (const candidate of candidates) {
      if (routes.length >= limit) break;
      const cycle = this.#findCycle(candidate, 24);
      if (!cycle) continue;
      const signature = cycle
        .map((lane) => lane.id)
        .sort()
        .join("|");
      if (signatures.has(signature)) continue;
      signatures.add(signature);
      const values: number[] = [];
      for (const lane of cycle) {
        if (values.length === 0) values.push(lane.fromX, lane.fromZ);
        else if (
          Math.hypot((values.at(-2) ?? 0) - lane.fromX, (values.at(-1) ?? 0) - lane.fromZ) > 0.05
        )
          values.push(lane.fromX, lane.fromZ);
        values.push(lane.toX, lane.toZ);
      }
      const firstX = values[0] ?? 0;
      const firstZ = values[1] ?? 0;
      if (Math.hypot((values.at(-2) ?? 0) - firstX, (values.at(-1) ?? 0) - firstZ) > 0.05)
        values.push(firstX, firstZ);
      routes.push(
        createVehicleRoute(
          `cycle:${candidate.id}`,
          cycle.map((lane) => lane.id),
          [...new Set(cycle.map((lane) => lane.tileId))],
          values,
          Math.min(...cycle.map((lane) => lane.speedMps)),
        ),
      );
    }
    return routes;
  }

  #findCycle(start: LaneEdge, maxEdges: number): LaneEdge[] | undefined {
    const path: LaneEdge[] = [start];
    const visited = new Set([start.id]);
    let remainingExpansions = 512;
    const visit = (edge: LaneEdge): boolean => {
      if (path.length >= maxEdges || remainingExpansions <= 0) return false;
      remainingExpansions -= 1;
      const outgoing = this.#orderedSuccessors(edge);
      for (const next of outgoing) {
        if (next.endNode === start.startNode && path.length >= 3) {
          path.push(next);
          return true;
        }
        if (visited.has(next.id)) continue;
        visited.add(next.id);
        path.push(next);
        if (visit(next)) return true;
        path.pop();
        visited.delete(next.id);
      }
      return false;
    };
    return visit(start) ? path : undefined;
  }

  #orderedSuccessors(edge: LaneEdge): LaneEdge[] {
    const incomingX = edge.toX - edge.fromX;
    const incomingZ = edge.toZ - edge.fromZ;
    const incomingLength = Math.hypot(incomingX, incomingZ);
    return (this.#outgoing.get(edge.endNode) ?? [])
      .filter(
        (next) =>
          next.id !== edge.id &&
          !(next.physicalId === edge.physicalId && next.segmentIndex === edge.segmentIndex),
      )
      .map((next) => {
        const nextX = next.toX - next.fromX;
        const nextZ = next.toZ - next.fromZ;
        const nextLength = Math.hypot(nextX, nextZ);
        const cosine =
          incomingLength > 0 && nextLength > 0
            ? (incomingX * nextX + incomingZ * nextZ) / (incomingLength * nextLength)
            : -1;
        return { next, score: 1 - cosine };
      })
      .filter(({ score }) => score < 1.94)
      .sort((left, right) => left.score - right.score || left.next.id.localeCompare(right.next.id))
      .map(({ next }) => next);
  }
}
